import { type FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion } from 'framer-motion';

import BackButtonOutline from '@/assets/img/icons/back-button-outline.svg?react';
import VipIcon from '@/assets/img/icons/vip-crown.svg?react';
import { productOfDayRewardsConfig } from '@/common/config/product-of-day-config';
import { type ProductOfDayDataType } from '@/common/types/contract';
import { type TicketRewardType } from '@/common/types/lottery';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  H1,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { MotionPicture, Picture } from '@/components/ui/Picture/Picture';
import { SplitSubtitle } from '@/components/ui/SplitSubtitle/SplitSubtitle';
import { useAppSelector } from '@/redux/store';

import { rewardList } from '../../GuaranteedReward/config';
import { GuaranteedReward } from '../../GuaranteedReward/GuaranteedReward';
import ProductOfDayClaimButton from '../../ProductOfDayClaimButton';

import ArrowsRightAnimation from './ArrowsRightAnimation';

import styles from './styles.module.scss';

import greenWheelPng from '/img/contract/greenwheel.png';
import greenWheelWebp from '/img/contract/greenwheel.webp';
import purpleWheelPng from '/img/contract/purplewheel.png';
import purpleWheelWebp from '/img/contract/purplewheel.webp';

interface UpgradeScreenProps {
  onCancel: () => void;
  product: ProductOfDayDataType;
  type: 'upgrade' | 'rewards';
}

const UpgradeScreen: FC<UpgradeScreenProps> = ({ onCancel, product, type }) => {
  const { t } = useTranslation();
  const isVip = useAppSelector((state) => state.user.user?.VIP);
  const { secondModal, thirdModal } = useContext(ModalContext);

  const rewardsLength = product.rewardInfo.regular.reward.length;

  useEffect(() => {
    if (product.type === 'premium' && type === 'upgrade') {
      onCancel();
    }
  }, [product.type, onCancel, type]);

  const ticketText = {
    silverTicket: 'Silver ticket',
    goldTicket: 'Gold ticket',
    diamondTicket: 'Diamond ticket',
  };

  return (
    <>
      <BackButtonOutline
        onClick={onCancel}
        className="top-4 left-4 size-12 absolute z-10"
      />

      <H1 weight="bold" className="text-center mt-2 relative z-[2]">
        {t('Upgrade Reward')}
      </H1>
      <motion.div
        initial={{ gap: 24, marginTop: 24 }}
        className="self-stretch px-2 flex justify-center items-center"
      >
        <div className="relative flex">
          <MotionPicture
            initial={{
              width: 72,
              height: 72,
              filter: 'drop-shadow(-8px -4px 24px rgba(24, 26, 32, 0.50))',
            }}
            img={greenWheelPng}
            webp={greenWheelWebp}
            alt="green box"
          />
        </div>

        <motion.div
          initial={{ height: 32, gap: 0 }}
          className="h-full flex flex-col items-center"
        >
          <span className="flex-1 w-[1px] bg-white/20"></span>
          <ArrowsRightAnimation />
          <span className="flex-1 w-[1px] bg-white/20"></span>
        </motion.div>

        <div className="relative flex">
          <MotionPicture
            initial={{
              width: 72,
              height: 72,
              filter: 'drop-shadow(16px -16px 24px rgba(24, 26, 32, 0.50))',
            }}
            img={purpleWheelPng}
            webp={purpleWheelWebp}
            alt="elite box"
          />
        </div>
      </motion.div>
      <div
        className={cn(
          'flex flex-col gap-4 self-stretch flex-1 pb-2 w-full px-4',
          styles.scrollbarHide,
        )}
        style={{
          overflowY: 'auto',
        }}
      >
        <SplitSubtitle
          text={'Guaranteed reward'}
          className="mt-[28px] mb-[4px]"
        />
        <div className={styles.guaranteedReward}>
          {rewardList.map((props, index) => {
            const regular = product.rewardInfo.regular;
            const premium = product.rewardInfo.premium;

            if (index === 0) {
              const guaranteedReward = regular.guaranteedReward[0];
              props.title.text = `+${guaranteedReward.value}`;
              props.description.text =
                ticketText[guaranteedReward.type as TicketRewardType];
            }

            if (index === 1) {
              const guaranteedReward = premium.guaranteedReward[0];
              props.title.text = `+${guaranteedReward.value}`;
              props.description.text =
                ticketText[guaranteedReward.type as TicketRewardType];
            }

            if (index === 2) {
              props.title.text = `+${regular.extraStarPoints}`;
            }

            if (index === 3) {
              props.title.text = `+${premium.extraStarPoints}`;
            }

            return <GuaranteedReward key={props.id} {...props} />;
          })}
        </div>
        <SplitSubtitle text={'Chance of winning prizes'} className="my-[4px]" />
        {new Array(rewardsLength).fill(null).map((_, index) => {
          const regularReward = product.rewardInfo.regular.reward[index];
          const vipReward = product.rewardInfo.vip.reward[index];
          const premiumReward = product.rewardInfo.premium.reward[index];
          const isUsdt = regularReward.type === 'usdt';
          const premiumType =
            premiumReward.type as keyof typeof productOfDayRewardsConfig;
          const regularType =
            regularReward.type as keyof typeof productOfDayRewardsConfig;

          const isUnavailable = regularReward.value === 0 && !isVip;

          return (
            <div key={index} className="gap-4 items-center flex justify-end">
              <div className="flex justify-end flex-1 items-center gap-1">
                <div className="flex items-end flex-col gap-0.5">
                  <StringMain
                    className={cn(
                      'text-[1.125rem]',
                      isUnavailable && 'opacity-60',
                    )}
                    weight="bold"
                  >
                    {isUsdt && '$ '}
                    {vipReward.value}
                  </StringMain>
                  <StringXSmall className="!text-[0.6125rem] text-white/60">
                    {productOfDayRewardsConfig[regularType]?.label}
                  </StringXSmall>
                </div>
                <div className="size-12 relative">
                  <Picture
                    className={cn('size-full', isUnavailable && 'opacity-40')}
                    img={productOfDayRewardsConfig[regularType]?.icon.png}
                    webp={productOfDayRewardsConfig[regularType]?.icon.webp}
                    alt={regularReward.type}
                  />
                  {regularReward.value === 0 && (
                    <VipIcon className="size-4 top-0 right-0 absolute" />
                  )}
                </div>
              </div>
              <div
                className={cn(
                  styles.percent,
                  rewardsLength - 1 === index && styles.last,
                )}
              >
                <StringMain weight="bold" className="!text-white/60">
                  {product.probabilities[index]?.probability}%
                </StringMain>
              </div>
              <div className="flex flex-1 items-center gap-1">
                <Picture
                  className="size-12"
                  img={productOfDayRewardsConfig[premiumType]?.icon.png}
                  webp={productOfDayRewardsConfig[premiumType]?.icon.webp}
                  alt={premiumReward.type}
                />

                <div className="flex items-start flex-col gap-0.5">
                  <StringMain className={'text-[1.125rem]'} weight="bold">
                    {premiumReward.type === 'usdt' && '$ '}
                    {premiumReward.value}
                  </StringMain>
                  <StringXSmall className="!text-[0.6125rem] text-white/60 max-w-[45px]">
                    {productOfDayRewardsConfig[premiumType]?.label}
                  </StringXSmall>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {type === 'upgrade' ? (
        <MainButton
          onClick={() => {
            if (thirdModal.modalContent.renderKind === 'productOfDayModal') {
              thirdModal.openModal({
                renderKind: 'buyProductOfDayUpgrade',
                props: { product },
              });

              return;
            }
            secondModal.openModal({
              renderKind: 'buyProductOfDayUpgrade',
              props: { product },
            });
          }}
          text={t('Upgrade') + ' $' + product.upgradeCost.amountUsd}
          className="mb-4 min-h-12 !w-[calc(100%_-_2rem)] mx-auto"
        />
      ) : (
        <ProductOfDayClaimButton
          product={product}
          className="mb-4 min-h-12 !w-[calc(100%_-_2rem)] mx-auto"
        />
      )}
    </>
  );
};

export default UpgradeScreen;
